import React, { useEffect, useState } from 'react';
import { styled } from 'linaria/react';
import { HtmlBannerRowItem } from './HtmlBannerRowItem';
import { theme } from '../../../Theme';
import useVisibilitySettings from '../../../../utils/useVisibilitySettings';
import { CustomArrow, Dot, Dots } from '../Slider/Slider';
import { useIntl } from '@jetshop/intl';
import { css, cx } from 'linaria';

const rowWidthStyle = css`
  display: block;
  max-width: 90rem;
  width: 100%;
  margin: 0 auto;

  &.full-width {
    max-width: 100%;
  }
`;

const HtmlBannerRowWrapper = styled('div')`
  display: grid;
  grid-template-columns: repeat(${props => props.mobilecolumns}, 1fr);
  gap: ${props => props.gap};
  width: 100%;
  max-width: ${props => props.maxwidth};
  margin: auto;

  ${theme.above.lg} {
    grid-template-columns: repeat(${props => props.desktopcolumns}, 1fr);
  }
`;

/* No slick-track — fixed aspect box, fades only (kills ~0.3 CLS from slick init) */
const CarouselRoot = styled('div')`
  position: relative;
  width: 100%;
  overflow: hidden;
  /* mobile first — desktop overridden above lg */
  aspect-ratio: var(--banner-aspect-mobile, 4 / 5);

  ${theme.above.lg} {
    aspect-ratio: var(--banner-aspect-desktop, 12 / 5);
  }

  .html-banner-row-item {
    height: 100%;
  }

  [data-flight-image-container] {
    position: absolute !important;
    inset: 0;
    width: 100% !important;
    height: 100% !important;
    padding-bottom: 0 !important;
  }

  ${theme.below.lg} {
    .slick-arrow {
      display: none;
    }
  }
`;

const SlideStage = styled('div')`
  position: absolute;
  inset: 0;
`;

const Slide = styled('div')`
  position: absolute;
  inset: 0;
  opacity: 0;
  transition: opacity 500ms ease;
  pointer-events: none;
  z-index: 0;

  &.active {
    opacity: 1;
    pointer-events: auto;
    z-index: 1;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const parseAspectParts = (value, fallback = '12:5') => {
  const raw = typeof value === 'string' && value ? value : fallback;
  const [w, h] = String(raw)
    .split(/[^\d]+/)
    .map(Number);
  if (!w || !h) {
    const [fw, fh] = fallback.split(/[^\d]+/).map(Number);
    return { w: fw || 12, h: fh || 5 };
  }
  return { w, h };
};

const BannerCarousel = ({
  slides,
  desktopAspectW,
  desktopAspectH,
  mobileAspectW,
  mobileAspectH,
  slideLabel
}) => {
  const [index, setIndex] = useState(0);
  const count = slides.length;

  useEffect(() => {
    if (count <= 1) return undefined;
    const id = setInterval(() => {
      setIndex(i => (i + 1) % count);
    }, 3000);
    return () => clearInterval(id);
  }, [count]);

  const go = delta => {
    setIndex(i => (i + delta + count) % count);
  };

  return (
    <CarouselRoot
      className="html-banner-row-carousel"
      style={{
        '--banner-aspect-desktop': `${desktopAspectW} / ${desktopAspectH}`,
        '--banner-aspect-mobile': `${mobileAspectW} / ${mobileAspectH}`
      }}
    >
      <SlideStage>
        {slides.map((slide, i) => (
          <Slide
            key={i}
            className={i === index ? 'active' : undefined}
            aria-hidden={i !== index}
          >
            {slide}
          </Slide>
        ))}
      </SlideStage>

      {count > 1 && (
        <>
          <CustomArrow
            className="slick-arrow slick-prev"
            onClick={() => go(-1)}
          />
          <CustomArrow
            className="slick-arrow slick-next"
            onClick={() => go(1)}
          />
          <Dots>
            {slides.map((_, i) => (
              <li key={i} className={i === index ? 'slick-active' : undefined}>
                <Dot
                  aria-label={`${slideLabel} ${i + 1}`}
                  type="button"
                  tabIndex={0}
                  onClick={() => setIndex(i)}
                />
              </li>
            ))}
          </Dots>
        </>
      )}
    </CarouselRoot>
  );
};

export const HtmlBannerRow = ({
  fullWidth,
  gap,
  sweden,
  denmark,
  norway,
  finland,
  netherlands,
  eu,
  desktopColumns,
  mobileColumns,
  asSlider,
  children
}) => {
  const visibilitySettings = {
    sweden: sweden?.value,
    denmark: denmark?.value,
    norway: norway?.value,
    finland: finland?.value,
    netherlands: netherlands?.value,
    eu: eu?.value
  };
  const doNotRender = useVisibilitySettings(visibilitySettings);
  const maxWidth = fullWidth?.value ? '100%' : '90rem';
  const elementGap = gap?.value;

  const translate = useIntl();
  const slideText = translate('Slide');

  if (doNotRender) {
    return null;
  }

  if (asSlider?.value) {
    const first = children?.[0]?.props;
    const desktop = parseAspectParts(first?.desktopImageAspect?.value, '12:5');
    const mobile = parseAspectParts(
      first?.mobileImageAspect?.value,
      first?.desktopImageAspect?.value || '4:5'
    );
    const slides = (children || []).map((child, index) => (
      <HtmlBannerRowItem
        key={index}
        onlyChild={{ value: true }}
        fillFrame
        {...child.props}
      />
    ));

    return (
      <div className={cx(rowWidthStyle, fullWidth?.value && 'full-width')}>
        <BannerCarousel
          slides={slides}
          desktopAspectW={desktop.w}
          desktopAspectH={desktop.h}
          mobileAspectW={mobile.w}
          mobileAspectH={mobile.h}
          slideLabel={slideText}
        />
      </div>
    );
  }

  const desktopCols =
    desktopColumns?.value ?? (children?.length >= 3 ? 3 : children?.length);
  const mobileCols = mobileColumns?.value ?? 1;

  return (
    <HtmlBannerRowWrapper
      className="html-banner-row"
      maxwidth={maxWidth}
      gap={elementGap}
      desktopcolumns={desktopCols}
      mobilecolumns={mobileCols}
    >
      {(children || []).map((child, index) => (
        <HtmlBannerRowItem
          key={index}
          onlyChild={{ value: children?.length === 1 }}
          {...child.props}
        />
      ))}
    </HtmlBannerRowWrapper>
  );
};
